import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CalendarClock, Plus, X, Check, Pencil, Trash2, AlertTriangle, FileSignature,
  HeartPulse, RefreshCw,
} from 'lucide-react';
import { echeancesApi, enfantsApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const TYPE = {
  contrat: { label: 'Contrat', pill: 'bg-navy-100 text-navy-700', icon: FileSignature },
  pai: { label: 'PAI', pill: 'bg-gold-100 text-gold-700', icon: HeartPulse },
  vaccin: { label: 'Vaccin', pill: 'bg-cyan-100 text-cyan-700', icon: HeartPulse },
  certificat: { label: 'Certificat', pill: 'bg-emerald-100 text-emerald-700', icon: HeartPulse },
  autre: { label: 'Autre', pill: 'bg-navy-50 text-navy-500', icon: CalendarClock },
};
const NIVEAU = {
  depasse: { label: 'Dépassé', pill: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  urgent: { label: 'Urgent', pill: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  a_venir: { label: 'À venir', pill: 'bg-navy-50 text-navy-400', dot: 'bg-navy-300' },
  traite: { label: 'Traité', pill: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500' },
};
const FILTRES = [
  { key: 'all', label: 'Toutes' },
  { key: 'contrat', label: 'Contrats' },
  { key: 'sante', label: 'Santé' },
  { key: 'autre', label: 'Autres' },
];
const SANTE = ['pai', 'vaccin', 'certificat'];

function fdate(d) { try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); } catch { return d; } }
function joursLabel(j) {
  if (j == null) return '';
  if (j < 0) return `${Math.abs(j)} j de retard`;
  if (j === 0) return "aujourd'hui";
  return `dans ${j} j`;
}

function EcheanceModal({ ech, enfants, onClose, onSaved }) {
  const { structures } = useApp();
  const isNew = !ech;
  const [form, setForm] = useState({
    type: ech?.type || 'pai', libelle: ech?.libelle || '', enfant_id: ech?.enfant_id || '',
    structure_id: ech?.structure_id || '', date_echeance: ech?.date_echeance || '', notes: ech?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.libelle || !form.date_echeance) return;
    setSaving(true);
    try {
      const payload = { ...form, enfant_id: form.enfant_id || null, structure_id: form.structure_id || null };
      if (isNew) await echeancesApi.create(payload); else await echeancesApi.update(ech.id, { ...ech, ...payload });
      onSaved(); onClose();
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={onClose}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-md animate-sheet" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="font-serif text-[20px] text-navy-900">{isNew ? 'Nouvelle échéance' : 'Modifier l\'échéance'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400"><X size={17} /></button>
        </div>
        <div className="px-6 pb-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Type</label>
              <select className="form-input rounded-xl" value={form.type} onChange={e => upd('type', e.target.value)}>
                <option value="pai">PAI</option><option value="vaccin">Vaccin</option>
                <option value="certificat">Certificat médical</option><option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Date d'échéance</label>
              <input type="date" className="form-input rounded-xl" value={form.date_echeance} onChange={e => upd('date_echeance', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">Libellé</label>
            <input className="form-input rounded-xl" value={form.libelle} onChange={e => upd('libelle', e.target.value)} placeholder="Ex : Renouvellement PAI asthme" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Enfant (optionnel)</label>
              <select className="form-input rounded-xl" value={form.enfant_id} onChange={e => upd('enfant_id', e.target.value)}>
                <option value="">—</option>
                {enfants.map(en => <option key={en.id} value={en.id}>{en.prenom} {en.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Structure</label>
              <select className="form-input rounded-xl" value={form.structure_id} onChange={e => upd('structure_id', e.target.value)}>
                <option value="">Vue générale</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </div>
          </div>
          <textarea className="form-input rounded-xl h-20 resize-none" value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Notes (optionnel)" />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="apple-btn-secondary">Annuler</button>
            <button onClick={submit} disabled={saving || !form.libelle || !form.date_echeance} className="apple-btn-primary">Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EcheancesPage() {
  const { selectedStructure } = useApp();
  const [items, setItems] = useState([]);
  const [enfants, setEnfants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState('all');
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedStructure ? { structure_id: selectedStructure } : {};
      const [e, en] = await Promise.all([echeancesApi.getAll(params), enfantsApi.getAll(params)]);
      setItems(e.data);
      setEnfants(en.data);
    } catch { /* noop */ }
    setLoading(false);
  }, [selectedStructure]);
  useEffect(() => { load(); }, [load]);

  const resume = useMemo(() => ({
    urgent: items.filter(i => i.niveau === 'urgent').length,
    depasse: items.filter(i => i.niveau === 'depasse').length,
  }), [items]);

  const visibles = items.filter(i => {
    if (filtre === 'all') return true;
    if (filtre === 'sante') return SANTE.includes(i.type);
    if (filtre === 'contrat') return i.type === 'contrat';
    return !['contrat', ...SANTE].includes(i.type);
  });

  const act = async (fn) => { try { await fn(); load(); } catch (err) { alert(err.response?.data?.error || 'Erreur'); } };

  return (
    <div className="-m-6 p-6 min-h-full bg-cream-200">
      <div className="max-w-4xl mx-auto pb-16">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="font-serif text-[32px] leading-tight text-navy-900">Échéances</h1>
            <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
            <p className="text-navy-400 text-[14px]">Fins de contrat, PAI, vaccins, certificats — à suivre et renouveler.</p>
          </div>
          <button onClick={() => setModal({ ech: null })} className="apple-btn-gold"><Plus size={16} /> Nouvelle échéance</button>
        </div>

        {/* Résumé alertes */}
        {(resume.depasse > 0 || resume.urgent > 0) && (
          <div className="flex gap-3 mb-4">
            {resume.depasse > 0 && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 rounded-xl px-4 py-2.5 text-[13px] font-medium">
                <AlertTriangle size={15} /> {resume.depasse} échéance(s) dépassée(s)
              </div>
            )}
            {resume.urgent > 0 && (
              <div className="flex items-center gap-2 bg-amber-50 text-amber-700 rounded-xl px-4 py-2.5 text-[13px] font-medium">
                <CalendarClock size={15} /> {resume.urgent} dans les 30 jours
              </div>
            )}
          </div>
        )}

        <div className="seg mb-4">
          {FILTRES.map(f => (
            <span key={f.key} onClick={() => setFiltre(f.key)} className={`seg-item ${filtre === f.key ? 'seg-item-active' : ''}`}>{f.label}</span>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-navy-300 py-16">Chargement…</div>
        ) : visibles.length === 0 ? (
          <div className="apple-card text-center py-14">
            <div className="w-14 h-14 rounded-2xl bg-cream-200 flex items-center justify-center mx-auto mb-3"><CalendarClock size={26} className="text-gold-500" /></div>
            <p className="text-navy-800 font-medium">Aucune échéance</p>
            <p className="text-[13px] text-navy-300 mt-1">Les fins de contrat s'affichent ici automatiquement.</p>
          </div>
        ) : (
          <div className="apple-card divide-y divide-cream-300/50 overflow-hidden">
            {visibles.map(i => {
              const t = TYPE[i.type] || TYPE.autre;
              const n = NIVEAU[i.niveau] || NIVEAU.a_venir;
              return (
                <div key={i.id} className="px-5 py-3.5 flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${n.dot}`} />
                  <t.icon size={17} className="text-navy-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[14px] text-navy-900 truncate">{i.libelle}</span>
                      <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full font-medium ${t.pill}`}>{t.label}</span>
                    </div>
                    <div className="text-[12px] text-navy-300 mt-0.5">
                      {i.enfant_nom ? `${i.enfant_nom} · ` : ''}{fdate(i.date_echeance)} · {joursLabel(i.jours_restants)}
                    </div>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${n.pill}`}>{n.label}</span>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {i.auto ? (
                      <span className="text-[11px] text-navy-300 px-2 hidden sm:inline">contrat</span>
                    ) : i.niveau === 'traite' ? (
                      <button onClick={() => act(() => echeancesApi.updateStatut(i.id, 'a_venir'))} title="Réactiver" className="p-2 rounded-lg text-navy-300 hover:bg-navy-50"><RefreshCw size={15} /></button>
                    ) : (
                      <button onClick={() => act(() => echeancesApi.updateStatut(i.id, 'traite'))} title="Marquer traité" className="p-2 rounded-lg text-navy-300 hover:bg-emerald-50 hover:text-emerald-600"><Check size={16} /></button>
                    )}
                    {!i.auto && <button onClick={() => setModal({ ech: i })} className="p-2 rounded-lg text-navy-300 hover:bg-navy-50"><Pencil size={15} /></button>}
                    {!i.auto && <button onClick={() => { if (window.confirm('Supprimer cette échéance ?')) act(() => echeancesApi.delete(i.id)); }} className="p-2 rounded-lg text-navy-200 hover:bg-red-50 hover:text-red-500"><Trash2 size={15} /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {modal && <EcheanceModal ech={modal.ech} enfants={enfants} onClose={() => setModal(null)} onSaved={load} />}
      </div>
    </div>
  );
}
